// components/maps/PharmacyMap.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import LocationMarker from './LocationMarker';
import MapControls from './MapControls';
import PharmacyMarker from './PharmacyMarker';
import { 
  Phone, 
  MapPin, 
  Star, 
  Navigation,
  ChevronRight,
  Truck
} from 'lucide-react';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Pharmacy Info Card Component
const PharmacyInfoCard = ({ pharmacy, onClose, onNavigate, onCall }) => {
  const isOpen = () => {
    if (!pharmacy.operatingHours) return null;
    
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[now.getDay()];
    const hours = pharmacy.operatingHours[currentDay];
    
    if (!hours || !hours.open || !hours.close) return null;
    
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const [openHour, openMin] = hours.open.split(':').map(Number);
    const [closeHour, closeMin] = hours.close.split(':').map(Number);
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;
    
    return currentTime >= openTime && currentTime <= closeTime;
  };

  const openStatus = isOpen();

  return (
    <div className="absolute bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white rounded-lg shadow-xl z-[1000] p-4">
      <button
        onClick={onClose}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-2xl leading-none"
      >
        ×
      </button>
      
      <div className="pr-6">
        <h3 className="font-semibold text-lg text-gray-900 mb-1">{pharmacy.name}</h3>
        
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {pharmacy.rating?.average > 0 && (
            <div className="flex items-center gap-1">
              <Star size={14} className="text-yellow-500 fill-current" />
              <span className="text-sm font-medium">{pharmacy.rating.average.toFixed(1)}</span>
              <span className="text-xs text-gray-500">({pharmacy.rating.count})</span>
            </div>
          )}
          
          {openStatus !== null && (
            <span className={`text-xs px-2 py-1 rounded-full ${
              openStatus ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {openStatus ? '● Open' : '● Closed'}
            </span>
          )}

          {pharmacy.deliveryService && (
            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
              <Truck size={10} />
              Delivery
            </span>
          )}
        </div>

        <p className="text-sm text-gray-600 mb-3 flex items-start gap-1">
          <MapPin size={14} className="mt-0.5 flex-shrink-0" />
          {pharmacy.address || 'Address not available'}
        </p>

        {pharmacy.distance && (
          <p className="text-sm text-green-600 font-medium mb-3">
            {pharmacy.distance.toFixed(1)} km away
          </p>
        )}

        <div className="flex gap-2">
          {pharmacy.phone && (
            <button
              onClick={() => onCall(pharmacy.phone)}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Phone size={16} />
              Call
            </button>
          )}
          
          <button
            onClick={() => onNavigate(pharmacy)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Navigation size={16} />
            Directions
          </button>
          
          <a 
            href={`/pharmacies/${pharmacy._id}`}
            className="flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ChevronRight size={16} />
          </a>
        </div>
      </div>
    </div>
  );
};

// Main PharmacyMap Component
const PharmacyMap = ({ 
  pharmacies = [],
  center,
  userLocation,
  selectedPharmacy: externalSelectedPharmacy,
  onPharmacySelect,
  showRadius = true,
  radiusKm = 25,
  className = "w-full h-[600px]",
  showClusters = true,
  showControls = true,
  onRequestLocation
}) => {
  const [selectedPharmacy, setSelectedPharmacy] = useState(externalSelectedPharmacy);
  const [mapLayer, setMapLayer] = useState('street');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapRef = useRef(null);
  const containerRef = useRef(null);

  // Map center - use user location or default to Nairobi
  const mapCenter = center ? [center.latitude, center.longitude] : 
                     userLocation ? [userLocation.latitude, userLocation.longitude] : 
                     [-1.2921, 36.8219];
  
  // Update selected pharmacy when external prop changes
  useEffect(() => {
    setSelectedPharmacy(externalSelectedPharmacy);
  }, [externalSelectedPharmacy]);

  // Handle marker click
  const handleMarkerClick = useCallback((pharmacy) => {
    setSelectedPharmacy(pharmacy);
    if (onPharmacySelect) {
      onPharmacySelect(pharmacy);
    }
  }, [onPharmacySelect]);

  // Handle navigation
  const handleNavigate = useCallback((pharmacy) => {
    if (pharmacy.location?.coordinates) {
      const [lng, lat] = pharmacy.location.coordinates;
      const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
      window.open(url, '_blank');
    }
  }, []);

  // Handle phone call
  const handleCall = useCallback((phone) => {
    window.location.href = `tel:${phone}`;
  }, []);

  // Recenter map
  const handleRecenter = useCallback(() => {
    if (mapRef.current && userLocation) {
      mapRef.current.setView([userLocation.latitude, userLocation.longitude], 14);
    }
  }, [userLocation]);

  // Toggle layer
  const handleLayerToggle = useCallback(() => {
    setMapLayer(prev => prev === 'street' ? 'satellite' : 'street');
  }, []);

  // Toggle fullscreen
  const handleFullscreenToggle = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // Map layers
  const tileLayers = {
    street: {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '© OpenStreetMap contributors'
    },
    satellite: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      attribution: '© Esri'
    }
  };

  // Legend items
  const legendItems = [
    { color: 'bg-green-500', label: 'Pharmacy', icon: null },
    { color: 'bg-blue-600', label: 'Delivery Available', icon: '🚚' },
    { color: 'bg-red-600', label: 'Selected', icon: null }
  ];

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Map Container */}
      <MapContainer
        center={mapCenter}
        zoom={userLocation ? 13 : 12}
        className="w-full h-full rounded-lg"
        ref={mapRef}
        zoomControl={false}
      >
        <TileLayer
          url={tileLayers[mapLayer].url}
          attribution={tileLayers[mapLayer].attribution}
        />
        
        <ZoomControl position="topright" />
        
        {/* Map Controls */}
        {showControls && (
          <MapControls
            userLocation={userLocation}
            onRecenter={handleRecenter}
            currentLayer={mapLayer}
            onLayerToggle={handleLayerToggle}
            legendItems={legendItems}
            showRecenter={true}
            showLayerToggle={true}
            showLegend={true}
            showCurrentLocation={!!onRequestLocation}
            onCurrentLocation={onRequestLocation}
            showFullscreen={true}
            isFullscreen={isFullscreen}
            onFullscreenToggle={handleFullscreenToggle}
          />
        )}

        {/* User Location Marker Component */}
        {userLocation && (
          <LocationMarker 
            position={userLocation}
            showRadius={showRadius}
            radiusKm={radiusKm}
            autoCenter={false}
          />
        )}

        {/* Pharmacy Markers */}
        {pharmacies.map((pharmacy) => (
          <PharmacyMarker
            key={pharmacy._id}
            pharmacy={pharmacy}
            isSelected={selectedPharmacy?._id === pharmacy._id}
            onClick={handleMarkerClick}
          />
        ))}
      </MapContainer>

      {/* Pharmacy Info Card */}
      {selectedPharmacy && (
        <PharmacyInfoCard
          pharmacy={selectedPharmacy}
          onClose={() => setSelectedPharmacy(null)}
          onNavigate={handleNavigate}
          onCall={handleCall}
        />
      )}

      {/* No pharmacies message */}
      {pharmacies.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-[999] rounded-lg">
          <div className="text-center">
            <MapPin size={48} className="mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600">No pharmacies to display</p>
            <p className="text-sm text-gray-500 mt-1">Try adjusting your search criteria</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmacyMap;